import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Empty,
  Layout,
  Popconfirm,
  Select,
  Space,
  Spin,
  Tree,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { getApiErrorMessage, serverFetch } from '@api/server';
import { getSpaces } from '../spaces';

const { Content } = Layout;
const { Text, Title } = Typography;

const SELECTED_SPACE_KEY = 'botva.articles.selectedSpaceId';

export const getArticles = (spaceId) => serverFetch('article', {
  queryParams: { spaceid: spaceId },
});

export const getArticle = (id) => serverFetch(`article/${id}`);

export const createArticle = (data) => serverFetch('article', {
  method: 'POST',
  bodyData: data,
});

export const updateArticle = (data) => serverFetch('article', {
  method: 'PUT',
  bodyData: data,
});

export const deleteArticle = (id) => serverFetch(`article/${id}`, {
  method: 'DELETE',
});

export const reorderArticles = (data) => serverFetch('articlereorder/update', {
  method: 'PUT',
  bodyData: data,
});

export const getArticleVersions = (articleId) => serverFetch('articleversion', {
  queryParams: { articleid: articleId },
});

export const getArticleVersion = (id) => serverFetch(`articleversion/${id}`);

export const getArticleChunks = (articleId) => serverFetch('articlechunks', {
  queryParams: { articleid: articleId },
});

export const recreateArticleChunks = (articleId) => serverFetch('articlechunks', {
  method: 'POST',
  queryParams: { id: articleId },
});

export const uploadArticleMedia = (data) => serverFetch('media', {
  method: 'POST',
  bodyData: data,
});

const buildPlainTree = (articles) => {
  const nodesById = new Map();

  articles.forEach((article) => {
    nodesById.set(article.id, {
      key: String(article.id),
      article,
      children: [],
    });
  });

  const roots = [];
  articles.forEach((article) => {
    const node = nodesById.get(article.id);
    if (article.parentid && nodesById.has(article.parentid)) {
      nodesById.get(article.parentid).children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (nodes) => {
    nodes.sort((left, right) => {
      if (left.article.idx !== right.article.idx) {
        return left.article.idx - right.article.idx;
      }
      return left.article.id - right.article.id;
    });
    nodes.forEach((node) => sortNodes(node.children));
  };

  sortNodes(roots);
  return roots;
};

const clonePlainTree = (nodes) => nodes.map((node) => ({
  key: node.key,
  article: node.article,
  children: clonePlainTree(node.children || []),
}));

const flattenTreeItems = (nodes, parentId = null) => {
  const items = [];
  nodes.forEach((node, index) => {
    const articleId = Number(node.key);
    items.push({
      id: articleId,
      parentid: parentId,
      idx: index,
    });
    if (node.children?.length) {
      items.push(...flattenTreeItems(node.children, articleId));
    }
  });
  return items;
};

const dropNodeInTree = (treeData, dragKey, dropKey, dropPosition, dropToGap) => {
  const data = clonePlainTree(treeData);
  let dragObj = null;

  const loop = (nodes, key, callback) => {
    nodes.forEach((node, index, arr) => {
      if (node.key === key) {
        callback(node, index, arr);
        return;
      }
      if (node.children?.length) {
        loop(node.children, key, callback);
      }
    });
  };

  loop(data, dragKey, (item, index, arr) => {
    arr.splice(index, 1);
    dragObj = item;
  });

  if (!dragObj) {
    return data;
  }

  if (!dropToGap) {
    loop(data, dropKey, (item) => {
      item.children = item.children || [];
      item.children.unshift(dragObj);
    });
    return data;
  }

  let dropIndex = 0;
  let dropParentChildren = data;
  loop(data, dropKey, (_item, index, arr) => {
    dropIndex = index;
    dropParentChildren = arr;
  });

  if (dropPosition === -1) {
    dropParentChildren.splice(dropIndex, 0, dragObj);
  } else {
    dropParentChildren.splice(dropIndex + 1, 0, dragObj);
  }

  return data;
};

const Articles = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [spaces, setSpaces] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedSpaceId, setSelectedSpaceId] = useState(() => {
    const saved = Number(localStorage.getItem(SELECTED_SPACE_KEY));
    return Number.isInteger(saved) && saved > 0 ? saved : null;
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [deletingArticleId, setDeletingArticleId] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState([]);

  const loadSpaces = useCallback(async () => {
    const result = await getSpaces();
    setSpaces(result || []);
    return result || [];
  }, []);

  const loadArticles = useCallback(async (spaceId) => {
    if (!spaceId) {
      setArticles([]);
      return;
    }

    const result = await getArticles(spaceId);
    setArticles(result || []);
    setExpandedKeys((result || []).map((article) => String(article.id)));
  }, []);

  const reload = useCallback(async () => {
    setError('');
    setIsLoading(true);
    try {
      const nextSpaces = await loadSpaces();
      let spaceId = selectedSpaceId;
      if (spaceId && !nextSpaces.some((space) => space.id === spaceId)) {
        spaceId = null;
        setSelectedSpaceId(null);
        localStorage.removeItem(SELECTED_SPACE_KEY);
      }
      await loadArticles(spaceId);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsLoading(false);
    }
  }, [loadArticles, loadSpaces, selectedSpaceId]);

  useEffect(() => {
    reload();
  }, [reload, location.state?.articlesRefreshKey]);

  const handleSpaceChange = async (spaceId) => {
    setSelectedSpaceId(spaceId || null);
    if (spaceId) {
      localStorage.setItem(SELECTED_SPACE_KEY, String(spaceId));
    } else {
      localStorage.removeItem(SELECTED_SPACE_KEY);
    }

    setError('');
    setIsLoading(true);
    try {
      await loadArticles(spaceId || null);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteArticle = async (articleId) => {
    setError('');
    setDeletingArticleId(articleId);
    try {
      await deleteArticle(articleId);
      setArticles((currentArticles) => (
        currentArticles.filter((article) => article.id !== articleId)
      ));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setDeletingArticleId(null);
    }
  };

  const plainTree = useMemo(() => buildPlainTree(articles), [articles]);

  const treeData = useMemo(() => {
    const mapNodes = (nodes) => nodes.map((node) => ({
      key: node.key,
      title: (
        <div className="article-tree-node">
          <span className="article-tree-node-title">{node.article.name}</span>
          <span
            className="article-tree-node-actions table-row-action"
            onClick={(event) => event.stopPropagation()}
          >
            <Popconfirm
              title="Удалить статью?"
              description="Статья будет скрыта (soft-delete)."
              okText="Удалить"
              cancelText="Отмена"
              okButtonProps={{ danger: true, loading: deletingArticleId === node.article.id }}
              onConfirm={() => handleDeleteArticle(node.article.id)}
            >
              <Button
                danger
                type="link"
                size="small"
                icon={<DeleteOutlined />}
                loading={deletingArticleId === node.article.id}
              >
                Удалить
              </Button>
            </Popconfirm>
          </span>
        </div>
      ),
      children: node.children?.length ? mapNodes(node.children) : undefined,
    }));

    return mapNodes(plainTree);
  }, [deletingArticleId, plainTree]);

  const handleDrop = async (info) => {
    const dragKey = info.dragNode.key;
    const dropKey = info.node.key;
    const dropPos = info.node.pos.split('-');
    const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);
    const previousArticles = articles;

    const nextTree = dropNodeInTree(
      plainTree,
      dragKey,
      dropKey,
      dropPosition,
      info.dropToGap,
    );
    const items = flattenTreeItems(nextTree);
    const articlesById = new Map(articles.map((article) => [article.id, article]));
    const nextArticles = items.map((item) => ({
      ...articlesById.get(item.id),
      parentid: item.parentid,
      idx: item.idx,
    }));

    setArticles(nextArticles);
    setError('');
    try {
      await reorderArticles({
        spaceid: selectedSpaceId,
        items,
      });
    } catch (requestError) {
      setArticles(previousArticles);
      setError(getApiErrorMessage(requestError));
    }
  };

  const spaceOptions = spaces.map((space) => ({
    value: space.id,
    label: space.name,
  }));

  return (
    <Content className="users-page">
      <div className="users-panel">
        <div className="panel-title users-title">
          <Space direction="vertical" size={0}>
            <Title level={5}>Статьи</Title>
            <Text type="secondary">{selectedSpaceId ? articles.length : '—'}</Text>
          </Space>
          <Space wrap>
            <Select
              className="articles-space-select"
              placeholder="Пространство"
              allowClear
              options={spaceOptions}
              value={selectedSpaceId}
              onChange={handleSpaceChange}
              style={{ minWidth: 220 }}
            />
            <Button icon={<ReloadOutlined />} onClick={reload}>
              Обновить
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={!selectedSpaceId}
              onClick={() => navigate(`/articles/new?spaceid=${selectedSpaceId}`)}
            >
              Создать
            </Button>
          </Space>
        </div>

        {error && (
          <Alert className="panel-alert" type="error" message={error} showIcon />
        )}

        <Spin spinning={isLoading}>
          {!selectedSpaceId ? (
            <Empty description="Выберите пространство" />
          ) : articles.length === 0 ? (
            <Empty description="Статей пока нет" />
          ) : (
            <Tree
              className="articles-tree"
              treeData={treeData}
              draggable
              blockNode
              expandedKeys={expandedKeys}
              onExpand={setExpandedKeys}
              onDrop={handleDrop}
              onSelect={(keys) => {
                if (keys[0]) {
                  navigate(`/articles/${keys[0]}`);
                }
              }}
            />
          )}
        </Spin>
      </div>
    </Content>
  );
};

export default Articles;
